import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { RenderPipeline } from '../renderer/RenderPipeline';
import type { SupabaseClient } from '../network/SupabaseClient';
import type { StellarRenderConfig } from '../config/StellarRenderConfig';
import type { SunLifecycleEntry } from '../systems/SunLifecycleTypes';
import { ContentLoader, type ClusterContent } from '../network/ContentLoader';
import { SunRenderer } from '../objects/SunRenderer';
import { PlanetRenderer } from '../objects/PlanetRenderer';
import { RingRenderer } from '../objects/RingRenderer';
import { MoonRenderer } from '../objects/MoonRenderer';
import { axialKey } from '../math/HexMath';
import type { ClusterManager } from './ClusterManager';

const SUNS_PER_CLUSTER = 6;
const PLANETS_PER_SUN = 8;

export class ContentManager implements GameModule {
  readonly name = 'ContentManager';
  readonly sunRenderer: SunRenderer;
  readonly planetRenderer: PlanetRenderer;
  readonly ringRenderer: RingRenderer;
  readonly moonRenderer: MoonRenderer;

  private _loader: ContentLoader;
  private _clusterManager: ClusterManager;
  private _contentRadius: number;
  private _engine: Engine | null = null;
  private _dirty = false;
  private _lifecycleDirty = false;
  private _rebuildVersion = 0;
  private _isVisible: ((q: number, r: number) => boolean) | null;

  private _clusterCoords: { col: number; row: number }[] = [];
  private _contents: ClusterContent[] = [];
  private _filteredContents: ClusterContent[] = [];
  private _sunHexKeys = new Set<number>();
  private _planetHexKeys = new Set<number>();

  constructor(
    gl: WebGL2RenderingContext,
    supabase: SupabaseClient,
    worldId: string,
    clusterManager: ClusterManager,
    pipeline: RenderPipeline,
    stellarConfig: StellarRenderConfig,
    hexRadius: number,
    contentRadius: number,
    isVisible?: (q: number, r: number) => boolean,
  ) {
    this._loader = new ContentLoader(supabase, worldId);
    this._clusterManager = clusterManager;
    this._contentRadius = contentRadius;
    this._isVisible = isVisible ?? null;

    const side = 2 * contentRadius + 1;
    const maxClusters = side * side;
    const maxSuns = maxClusters * SUNS_PER_CLUSTER;
    const maxPlanets = maxSuns * PLANETS_PER_SUN;

    const maxRings = Math.floor(maxPlanets * 0.1);
    const maxMoons = Math.floor(maxPlanets * 1.5);
    this.sunRenderer = new SunRenderer(gl, stellarConfig, hexRadius, maxSuns);
    this.planetRenderer = new PlanetRenderer(gl, stellarConfig, hexRadius, maxPlanets);
    this.ringRenderer = new RingRenderer(gl, stellarConfig, hexRadius, maxRings);
    this.moonRenderer = new MoonRenderer(gl, stellarConfig, hexRadius, maxMoons);

    pipeline.addRenderable(this.sunRenderer);
    pipeline.addRenderable(this.planetRenderer);
    pipeline.addRenderable(this.ringRenderer);
    pipeline.addRenderable(this.moonRenderer);
  }

  get sunHexKeys(): ReadonlySet<number> { return this._sunHexKeys; }
  get planetHexKeys(): ReadonlySet<number> { return this._planetHexKeys; }
  get rebuildVersion(): number { return this._rebuildVersion; }

  init(engine: Engine): void {
    this._engine = engine;
    engine.events.on('world:clusters-changed', () => {
      this._requestVisibleContent();
      this._dirty = true;
    });
    engine.events.on('fog:reveals-changed', () => { this._dirty = true; });
    engine.events.on<ReadonlyMap<string, SunLifecycleEntry>>('sun:lifecycle-update', (data) => {
      this.sunRenderer.setLifecycleData(data);
      this._lifecycleDirty = true;
    });
    this._requestVisibleContent();
  }

  update(_dt: number): void {
    if (this._dirty) {
      this._dirty = false;
      this._lifecycleDirty = false;
      this._rebuildRenderers();
      return;
    }
    if (this._lifecycleDirty) {
      this._lifecycleDirty = false;
      this.sunRenderer.updateFromContent(this._contents);
    }
  }

  dispose(): void {
    this._loader.dispose();
    this.sunRenderer.dispose();
    this.planetRenderer.dispose();
    this.ringRenderer.dispose();
    this.moonRenderer.dispose();
  }

  get loadedStars(): number { return this.sunRenderer.sunCount; }
  get loadedPlanets(): number { return this.planetRenderer.planetCount; }

  private _requestVisibleContent(): void {
    this._clusterCoords.length = 0;
    const camCol = this._clusterManager.cameraClusterCol;
    const camRow = this._clusterManager.cameraClusterRow;
    const r = this._contentRadius;

    this._clusterManager.forEachCluster((cluster) => {
      const dc = Math.abs(cluster.col - camCol);
      const dr = Math.abs(cluster.row - camRow);
      if (dc <= r && dr <= r) {
        this._clusterCoords.push({ col: cluster.col, row: cluster.row });
      }
    });

    this._loader.requestClusters(this._clusterCoords);
    setTimeout(() => { this._dirty = true; }, 500);
  }

  private _rebuildRenderers(): void {
    this._contents.length = 0;
    const camCol = this._clusterManager.cameraClusterCol;
    const camRow = this._clusterManager.cameraClusterRow;
    const r = this._contentRadius;

    this._clusterManager.forEachCluster((cluster) => {
      const dc = Math.abs(cluster.col - camCol);
      const dr = Math.abs(cluster.row - camRow);
      if (dc > r || dr > r) return;
      const content = this._loader.getContent(cluster.col, cluster.row);
      if (content) this._contents.push(content);
    });

    this._sunHexKeys.clear();
    this._planetHexKeys.clear();
    for (let c = 0; c < this._contents.length; c++) {
      const stars = this._contents[c].stars;
      for (let i = 0; i < stars.length; i++) {
        const s = stars[i];
        if (s.axial_q != null && s.axial_r != null) {
          this._sunHexKeys.add(axialKey(s.axial_q, s.axial_r));
        }
      }
      const planets = this._contents[c].planets;
      for (let i = 0; i < planets.length; i++) {
        const p = planets[i];
        if (p.axial_q != null && p.axial_r != null) {
          this._planetHexKeys.add(axialKey(p.axial_q, p.axial_r));
        }
      }
    }

    this.sunRenderer.updateFromContent(this._contents);
    this._engine?.events.emit('sun:stars-available', this._contents);

    if (this._isVisible) {
      this._buildFilteredContent();
      this.planetRenderer.updateFromContent(this._filteredContents);
      this.ringRenderer.updateFromContent(this._filteredContents);
      this.moonRenderer.updateFromContent(this._filteredContents);
    } else {
      this.planetRenderer.updateFromContent(this._contents);
      this.ringRenderer.updateFromContent(this._contents);
      this.moonRenderer.updateFromContent(this._contents);
    }

    this._rebuildVersion++;
  }

  private _buildFilteredContent(): void {
    this._filteredContents.length = 0;
    const check = this._isVisible!;

    for (let c = 0; c < this._contents.length; c++) {
      const src = this._contents[c];
      const revealed: typeof src.planets = [];
      for (let i = 0; i < src.planets.length; i++) {
        const p = src.planets[i];
        if (p.axial_q != null && p.axial_r != null && check(p.axial_q, p.axial_r)) {
          revealed.push(p);
        }
      }
      this._filteredContents.push({ stars: src.stars, planets: revealed });
    }
  }
}
